import { Button, Checkbox, Divider, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import 'moment/locale/en-gb';
import { ArrowLeftOutlined, EditOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar.tsx';
import { useHealthmetrictypesWithMeasurements } from '../../fetchHooks/useHealthmetrictypesWithMeasurements.js';
import { useUserProfile } from '../../fetchHooks/useUserProfile.ts';
import { getFontClassName } from '../../utils/fontUtils';
import {
  RichTextField,
  SensitiveTextField,
  formatDateOnly,
  getHealthMetricDescription,
  waitForNSeconds,
} from '../../utils/utilFunctions.tsx';
import { EditMyday } from './EditMyday.jsx';
import HealthMetricGraphs from './HealthMetricGraphs.jsx';
import './MydayDetails.scss';
import { Images } from '../../components/Images/Images.tsx';
import { useMyday } from '../../fetchHooks/useMyday.ts';
import type { MydayType } from '../../fetchHooks/useMyday.ts';
import { exportMydayToPDF } from '../../utils/exportMydayToPDF';
import { Files } from './Files.tsx';
import { InteractiveMeasurementsAnalysis } from './InteractiveMeasurementsAnalysis.tsx';
import { MeasurementsAnalysis } from './MeasurementsAnalysis.tsx';

type MydayDetailsProps = {
  mydayId: number;
  isExport: boolean;
};

const MydayDetails = ({ mydayId, isExport = false }: MydayDetailsProps) => {
  const { t, i18n } = useTranslation();
  const [showBloodworkDescription, setShowBloodworkDescription] = useState(false);
  const { id: paramId } = useParams();
  const id = isExport ? mydayId : paramId;
  const { data: myday, isLoading, error, refetch } = useMyday(id as string);
  const [hideText, setHideText] = useState(false);
  const [showEditMydayModal, setShowEditMydayModal] = useState<MydayType | undefined>(undefined);
  const { data: userProfile } = useUserProfile();
  const fontClassName = getFontClassName(userProfile?.settings?.preferredFont);
  const { data: healthmetrictypes } = useHealthmetrictypesWithMeasurements();
  const navigate = useNavigate();
  const isSimplified = userProfile?.services?.includes('simplified');
  const exportContainerRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showLargeImages, setShowLargeImages] = useState(false);

  useEffect(() => {
    !isExport && !hideText && waitForNSeconds(30, () => setHideText(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideText, isExport]);

  const handleExportToPDF = async () => {
    if (!myday || !exportContainerRef.current) return;

    setIsExporting(true);
    // Show large images before export
    setShowLargeImages(true);
    try {
      // Wait a bit for images to load
      await new Promise((resolve) => setTimeout(resolve, 300));
      await exportMydayToPDF(myday, exportContainerRef.current);
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
      setShowLargeImages(false);
    }
  };

  if (isLoading) return <div className="loading-modal">{t('common.loading')}</div>;
  if (error) {
    const e = JSON.parse(error?.message);
    console.log('error: ', e);
    return (
      <div className="loading-modal">
        <p style={{ textAlign: 'center' }}>
          {e.message}: {e.error}
        </p>
      </div>
    );
  }
  if (!myday) return <p>{t('errors.genericError')}</p>;
  const { description } = myday;
  const date = new Date(myday?.date);
  const onOk = () => {
    setShowEditMydayModal(undefined);
    refetch();
  };

  if (isExport)
    return (
      <div>
        <div className="myday-details-container">
          <div className="myday-details-title">
            <h1 style={{ marginBottom: 8, marginTop: 0 }}>{description}</h1>
            <h4 style={{ marginTop: 0 }}>{formatDateOnly(date, i18n.language)} </h4>
            {/* NOTE TEXT: */}
            <RichTextField className={fontClassName} text={myday?.note} />
          </div>
          {/* MEASUREMENTS: */}
          <div>
            {!!myday?.measurements?.length && myday?.measurements?.length > 0 && (
              <>
                <h3>{t('myday.measurements')}:</h3>
                <ol style={{ paddingInlineStart: 20 }}>
                  {myday?.measurements?.map?.((m) => (
                    <li key={m.id}>
                      {getHealthMetricDescription(m.healthMetricType, i18n.language)}:{' '}
                      <RichTextField text={m.value.toString()} />
                      {' ' + m.healthMetricType?.unit}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>
        {myday.file_count && myday.file_count > 0 && <Images isExport={isExport} myday={myday} />}
        <Divider />
      </div>
    );

  return (
    <div>
      <Navbar />
      <Tooltip title={t('common.back')}>
        <Button size="large" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} ghost className="back-button">
          {t('common.back')}
        </Button>
      </Tooltip>

      <div ref={exportContainerRef}>
        <div className="centered-container">
          <div className="myday-details-container">
            <div className="myday-details-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <h1 style={{ marginBottom: 8, marginTop: 0 }}>{description}</h1>
                {!isExport && (
                  <>
                    <Tooltip title={t('myday.editLifeLogTooltip')}>
                      <Button
                        shape="round"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setShowEditMydayModal(myday)}
                        ghost
                        className="edit-button"
                      />
                    </Tooltip>
                    <Tooltip title={t('myday.exportToPDF')}>
                      <Button
                        shape="round"
                        size="small"
                        icon={<FilePdfOutlined />}
                        onClick={handleExportToPDF}
                        loading={isExporting}
                        disabled={isExporting || !myday}
                        ghost
                        className="edit-button"
                      />
                    </Tooltip>
                  </>
                )}
              </div>
              <h4 style={{ marginTop: 0 }}>{formatDateOnly(date, i18n.language)} </h4>
            </div>
          </div>
          {/* NOTE TEXT: */}
          <div className="inner-centered-container tiptap text-10">
            <RichTextField className={fontClassName} text={myday?.note} onClick={() => setHideText((prev) => !prev)} />
          </div>
          {/* MEASUREMENTS: */}
          {!isSimplified && (
            <div>
              {!!myday?.measurements?.length && myday?.measurements?.length > 0 && (
                <>
                  <MeasurementsAnalysis myday={myday} />
                  <InteractiveMeasurementsAnalysis myday={myday} onConversationUpdate={refetch} />
                  <h3>{t('myday.measurements')}:</h3>
                  <ol style={{ paddingInlineStart: 20 }}>
                    {myday?.measurements?.map?.((m) => (
                      <li key={m.id}>
                        {getHealthMetricDescription(m.healthMetricType, i18n.language)}:{' '}
                        <SensitiveTextField
                          text={m.value.toString() + m.healthMetricType?.unit}
                          show={!hideText}
                          onClick={() => setHideText((prev) => !prev)}
                        />
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          )}
        </div>
        {!!myday.file_count && (
          <div className="centered-container">
            <Images myday={myday} isExport={showLargeImages} />
            <Files myday={myday} compact />
          </div>
        )}
        {!!myday?.measurements?.length && myday?.measurements?.length > 0 && (
          <div className="centered-container">
            <h1 style={{ marginBottom: '0' }}>{t('myday.measurementGraph')}</h1>
            <Checkbox checked={showBloodworkDescription} onChange={() => setShowBloodworkDescription((prev) => !prev)}>
              {t('myday.showBloodworkDescription')}
            </Checkbox>
            <div className="centered-container">
              <HealthMetricGraphs
                selectedGraphs={healthmetrictypes?.filter((healthMetric) =>
                  myday?.measurements?.some((measurement) => measurement.healthMetricType?.id === healthMetric.id),
                )}
                showBloodworkDescription={showBloodworkDescription}
              />
            </div>
          </div>
        )}
      </div>
      {showEditMydayModal && (
        <EditMyday
          showModal={!!showEditMydayModal}
          onClose={() => setShowEditMydayModal(undefined)}
          onOk={onOk}
          userProfile={userProfile}
          myday={showEditMydayModal as MydayType}
        />
      )}
    </div>
  );
};

export default MydayDetails;
